/**
 * TanStack Query — Step 12's data layer, with the plan's defaults:
 *  - never retry 4xx (the default would turn one 403 into three and a slow error)
 *  - refetchOnWindowFocus: the cheapest "feels live" there is
 *  - 401 stays the fetch helpers' business, NOT Query's — apiFetch redirects to
 *    login with returnTo; Query just sees the throw.
 */
import { QueryCache, QueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { ApiError } from './api';

export const queryClient = new QueryClient({
  // Two error treatments, deliberately different: a BACKGROUND refetch failing
  // gets a toast (the page still shows the last good data — an inline error
  // would wrongly blank it); an INITIAL load failing renders inline with Retry.
  queryCache: new QueryCache({
    onError: (error, query) => {
      if (query.state.data !== undefined) {
        toast.error(error instanceof ApiError ? error.userMessage : 'A background refresh failed.');
      }
    },
  }),
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      gcTime: 5 * 60_000,
      refetchOnWindowFocus: true,
      retry: (failureCount, error) =>
        !(error instanceof ApiError && error.status >= 400 && error.status < 500) && failureCount < 2,
    },
  },
});

/** ONE key factory — inline queryKey arrays are how an invalidation silently
 *  misses one of two spellings of the same key (audit:query-keys will enforce). */
export const qk = {
  /** Step 153 — Entra group → role mappings, and the group search. */
  directoryMappings: ['directory', 'role-mappings'] as const,
  directoryEntraGroups: (q: string) => ['directory', 'entra-groups', q] as const,
  /** Step 101 — CAB boards: the admin list, and the picker agents see. */
  approvalBoards: ['approval-boards'] as const,
  approvalBoardPicker: ['approval-boards', 'picker'] as const,
  /** Step 143 — change-freeze windows (admin list; agent read by range). */
  freezeWindows: ['freeze-windows'] as const,
  freezeWindowsRange: (from: number, to: number) => ['freeze-windows', from, to] as const,
  /** Step 129 — which settings cards are configured. */
  adminConfigured: ['admin-configured'] as const,
  /** Step 165 — the setup wizard's status: what is done, what the libraries would add. */
  setupStatus: ['setup-status'] as const,
  /** Step 165 — the org's stored mail row, only to learn whether a secret is stored. */
  setupMailStored: ['setup-mail-stored'] as const,
  /** Step 104 — requesters with their departments, locations and profile fields. */
  adminRequesters: ['admin-requesters'] as const,
  /** Step 104 — the desk's own department / location lists. */
  orgList: (kind: 'departments' | 'locations') => ['org-list', kind] as const,
  /** Step 95 — service desk settings and the next ticket number. */
  serviceDesk: ['service-desk'] as const,
  /** Step 149 — the admin's view of the properties-rail layout. */
  propertiesLayoutAdmin: ['form-layouts', 'ticket'] as const,
  /** Step 99 — roles and the permission matrix. */
  roles: ['roles'] as const,
  /** Step 96 — what this desk sends, and to whom. */
  notificationSettings: ['notification-settings'] as const,
  tickets: {
    all: ['tickets'] as const,
    list: (params: string) => ['tickets', 'list', params] as const,
    detail: (id: string) => ['tickets', 'detail', id] as const,
    /** Step 45's tabs. Nested under the ticket so invalidating `detail` does
     *  not blow them away, but a targeted invalidate still can. */
    activity: (id: string) => ['tickets', 'activity', id] as const,
    /** Step 114 — the status timeline. */
    statusHistory: (id: string) => ['tickets', 'status-history', id] as const,
    children: (id: string) => ['tickets', 'children', id] as const,
    resolution: (id: string) => ['tickets', 'resolution', id] as const,
    problem: (id: string) => ['tickets', 'problem', id] as const,
    change: (id: string) => ['tickets', 'change', id] as const,
    /** Step 54 — the outage record and the review written about it. */
    majorIncident: (id: string) => ['tickets', 'major-incident', id] as const,
    pir: (id: string) => ['tickets', 'pir', id] as const,
    /** Step 55 — what a message said before each edit. Nested under the
     *  ticket so invalidating the detail does not blow it away. */
    /** Step 55 — the ticket's own files. */
    attachments: (id: string) => ['tickets', 'attachments', id] as const,
    /** Step 154 — what a reply here would ALSO reach. */
    downstream: (id: string) => ['tickets', 'downstream', id] as const,
    /** Step 149 — one layout for the org, so no id. */
    propertiesLayout: ['tickets', 'properties-layout'] as const,
    revisions: (ticketId: string, messageId: string) =>
      ['tickets', 'revisions', ticketId, messageId] as const,
    changeCalendar: (from: number, to: number) => ['tickets', 'change-calendar', from, to] as const,
    associations: (id: string) => ['tickets', 'associations', id] as const,
  },
  people: ['people'] as const,
  /** Step 55 — the org's own department list. */
  departments: ['departments'] as const,
  /** Steps 53/130 — one person page for requesters and agents alike. */
  person: (id: string) => ['people', 'detail', id] as const,
  /** Step 52. */
  apiKeys: { all: ['api-keys'] as const, roles: ['api-keys', 'roles'] as const },
  /** Step 51. `live` is read by the shell on every page, so it is its own key
   *  and not derived from the admin list, which most agents cannot fetch. */
  announcements: {
    live: ['announcements', 'live'] as const,
    all: ['announcements'] as const,
    /** The portal asks a different endpoint and gets a different answer, so
     *  it must not share the agent banner's cache entry. */
    portal: ['announcements', 'portal'] as const,
  },
  /** Step 50 — dashboards. `data` is keyed by what it ASKS, not by which
   *  widget asked, so two cards pointing at the same view share one fetch. */
  dashboards: {
    all: ['dashboards'] as const,
    data: (viewId: string, groupBy: string) => ['dashboards', 'data', viewId, groupBy] as const,
  },
  /** Step 47 — approvals. `mine` is its own key because the inbox is read
   *  from the shell (a count badge) as well as its own page. */
  approvals: {
    mine: ['approvals', 'mine'] as const,
    entity: (type: string, id: string) => ['approvals', 'entity', type, id] as const,
    delegations: ['approvals', 'delegations'] as const,
    /** The PORTAL's own read. A separate key from `mine` on purpose: it is a
     *  different endpoint on a different auth domain, and sharing the cache
     *  entry would let an agent session's data answer a requester's page. */
    portalMine: ['portal', 'approvals'] as const,
    manage: ['approvals', 'manage'] as const,
  },
  branding: ['branding'] as const,
  brandingSettings: ['branding-settings'] as const,
  mailSettings: ['mail-settings'] as const,
  /** Step 174 — my own signature and reply template. One key, because the
   *  profile editor and the ticket composer read the same thing and a second
   *  spelling would mean saving it left the composer stale. */
  mySignature: ['my-signature'] as const,
  mailboxes: ['mailboxes'] as const,
  // Step 31 — SLA admin.
  // Step 94 — the item editor asks for a DIFFERENT scope than the Ticket
  // fields page, so it needs its own key or the two share a cache entry.
  sharedFieldDefinitions: ['field-definitions', 'shared'] as const,
  /** Every scope. The item editor resolves a field ROW by id, and a question
   *  created inline is scoped to its item — so a shared-only list cannot name
   *  it. Shares the 'field-definitions' prefix, so one invalidate covers all. */
  allFieldDefinitions: ['field-definitions', 'all'] as const,
  businessCalendars: ['business-calendars'] as const,
  slaPolicies: ['sla-policies'] as const,
  // Step 32 — rules and their execution log are separate keys: the log is
  // fetched only when its tab is open.
  automations: ['automations'] as const,
  automationRuns: ['automation-runs'] as const,
  automationMeta: ['automation-meta'] as const,
  automationTrash: ['automation-trash'] as const,
  // Step 34 — the report is keyed by its filters, so changing the range or
  // the group refetches instead of showing last week's numbers under this
  // week's heading.
  serviceDeskReport: (from: number, to: number, groupId: string | null) =>
    ['reports', 'service-desk', from, to, groupId] as const,
  ssoSettings: ['sso-settings'] as const,
  // Step 39 — directory sync state and the sample of who landed.
  // Step 43 — the templates an agent can pick from.
  ticketStatuses: ['ticket-statuses'] as const,
  ticketTemplates: ['ticket-templates'] as const,
  adminTicketTemplates: ['admin-ticket-templates'] as const,
  directorySync: ['directory-sync'] as const,
  directoryRecent: ['directory-recent'] as const,
  adminGroups: ['admin-groups'] as const,
  /** Step 163 — agents, with their roles and groups. */
  adminAgents: ['admin-agents'] as const,
  /** Step 81 — roles and their ticket scope. */
  adminRoles: ['admin-roles'] as const,
  /** Step 125 — the signed-in agent's own feed. */
  notifications: ['notifications'] as const,
  /** Step 79 — the tenant's noun overrides, as edited. */
  vocabulary: ['vocabulary'] as const,
  /** Step 77 — one item's bundled additional items. */
  itemLinks: (id: string) => ['item-links', id] as const,
  orgGroups: ['org-groups'] as const,
  // Step 69 — requester groups, the requester-side axis of item visibility.
  requesterGroups: ['requester-groups'] as const,
  /** The requester directory, for naming a group's hand-added members. */
  requesterDirectory: ['requester-directory'] as const,
  /** The distinct directory values a membership rule can match on. */
  requesterAttributes: ['requester-attributes'] as const,
  filterableFields: ['filterable-fields'] as const,
  queueAgents: ['queue-agents'] as const,
  portalCatalogue: ['portal-catalogue'] as const,
  portalItemForm: (id: string) => ['portal-item-form', id] as const,
  portalTickets: ['portal-tickets'] as const,
  portalTicket: (id: string) => ['portal-ticket', id] as const,
  holdReasons: ['hold-reasons'] as const,
  audit: (params: string) => ['audit', params] as const,
  emailLog: {
    all: ['email-log'] as const,
    list: (params: string) => ['email-log', params] as const,
  },
  views: ['views'] as const,
  /** The ADMIN listing — a different question and a different shape from
   *  `views`, so a different key: sharing one would make the rail and the
   *  admin page fight over the same cache entry. */
  viewsManage: ['views', 'manage'] as const,
  kbTree: ['kb-tree'] as const,
  kbArticles: {
    all: ['kb-articles'] as const,
    list: (params: string) => ['kb-articles', params] as const,
  },
  kbFolderArticles: (folderId: string) => ['kb-folder-articles', folderId] as const,
  kbArticle: (id: string) => ['kb-article', id] as const,
  /** Step 122 — an article's version history. */
  kbVersions: (id: string) => ['kb-article', id, 'versions'] as const,
  kbVersion: (id: string, version: number) => ['kb-article', id, 'versions', version] as const,
  portalProfile: ['portal-profile'] as const,
  portalKbTree: ['portal-kb-tree'] as const,
  portalKbArticle: (slug: string) => ['portal-kb-article', slug] as const,
  cannedResponses: ['canned-responses'] as const,
  fieldDefinitions: ['field-definitions'] as const,
  fieldUsage: (id: string) => ['field-usage', id] as const,
  formLayouts: ['form-layouts'] as const,
  catalogueCategories: ['catalogue-categories'] as const,
  // Keyed by scope — the admin's all-items list and the browse page's
  // active-only list are DIFFERENT data; one bare key let either hydrate
  // the other (draft items bleeding into browse, stale popular shelves).
  catalogueItems: {
    all: ['catalogue-items'] as const,
    list: (scope: 'all' | 'active') => ['catalogue-items', scope] as const,
  },
  /** Step 70 — the prefill is resolved for a PERSON, so the person is part
   *  of the identity of the answer. Without them in the key, switching the
   *  requester picker would serve the previous person's details from cache. */
  catalogueItemForm: (itemId: string, forEmail?: string) =>
    ['catalogue-item-form', itemId, forEmail ?? ''] as const,
  globalSearch: (q: string) => ['global-search', q] as const,
  globalSearchEntity: (q: string, entity: string) => ['global-search', q, entity] as const,
  portalSearch: (q: string) => ['portal-search', q] as const,
  portalSearchEntity: (q: string, entity: string) => ['portal-search', q, entity] as const,
  formLayoutPreview: (layoutId: string, sectionsKey: string, surface: string) =>
    ['form-layout-preview', layoutId, sectionsKey, surface] as const,
};
